import { formatNumber } from '@edge/spark/utils'
import { DataField, type ContactLogStats, type DataFieldValues, type VehicleStats } from '@edge/spark/io-dashboards'

const NULL_RESULT = '--'

export type StatsValue = Omit<ContactLogStats & VehicleStats, '__typename'> | undefined | null
export type FormattedStats = Record<keyof StatsValue, string>

export const formatStatsValue = (stats?: StatsValue, dataField?: DataFieldValues): string => {
    if (
        !stats ||
        !dataField ||
        dataField === 'none' ||
        // @ts-expect-error just in case
        dataField === '__typename'
    ) {
        return NULL_RESULT
    }

    const value = stats[dataField]

    if (!value || typeof value !== 'number' || value < 0) {
        // Includes zero and NaN
        return NULL_RESULT
    }

    // Format: `${CONVERSION / 100}%`
    if (dataField === DataField.CONVERSION) {
        return formatNumber(value / 100, { style: 'percent' }) || NULL_RESULT
    }

    if (dataField === DataField.AVG_SALE_AMOUNT) {
        return (
            formatNumber(value, {
                style: 'currency',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }) || NULL_RESULT
        )
    }

    if (dataField === DataField.SUM_SALE_AMOUNT) {
        return value > 10_000_000
            ? formatNumber(value, {
                  style: 'currency',
                  notation: 'compact',
                  compactDisplay: 'short',
              })
            : formatNumber(value, {
                  style: 'currency',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
              }) || NULL_RESULT
    }

    if (dataField === DataField.TOTAL) {
        return formatNumber(value, { maximumFractionDigits: 0 }) || NULL_RESULT
    }

    if (dataField === DataField.RAN) {
        return formatNumber(value, { maximumFractionDigits: 0 }) || NULL_RESULT
    }

    if (dataField === DataField.SOLD) {
        return formatNumber(value, { maximumFractionDigits: 0 }) || NULL_RESULT
    }

    if (dataField === DataField.COMPLETED) {
        return formatNumber(value, { maximumFractionDigits: 0 }) || NULL_RESULT
    }

    return NULL_RESULT
}

export const formatStats = (stats?: StatsValue): FormattedStats => {
    return {
        total: formatStatsValue(stats, 'total'),
        ran: formatStatsValue(stats, 'ran'),
        sold: formatStatsValue(stats, 'sold'),
        sumSaleAmount: formatStatsValue(stats, 'sumSaleAmount'),
        avgSaleAmount: formatStatsValue(stats, 'avgSaleAmount'),
        conversion: formatStatsValue(stats, 'conversion'),
        completed: formatStatsValue(stats, 'completed'),
    }
}

export default formatStats
